import { useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import qs from 'query-string';
import { useStringsContext } from '../../../contexts/strings';
import { useAllPlayers, useAllTeams } from '../../../hooks/team';
import { useUserAuth } from '../../../contexts/auth';
import { PlayerState, PLAYER_TABS } from '../squad-database/player-container';
import { FilterType, SquadPlayer } from './types';
import RemovePlayerModal from '../squad-database/components/player/remove-player-modal';
import Filters from './components/filters';
import { Filter, Operation, Field } from './types';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { get, isEmpty } from 'lodash';
import PlayerList from './components/player-list';
import SceneHeader from '../../common/scene/scene-header';
import ScenePage from '../../common/scene/scene-page';
import { FormControl, InputLabel } from '@mui/material';

const FieldMap = {
  [Field.League]: (player: SquadPlayer) => get(player, 'activeCurrentClub.league'),
  [Field.Team]: (player: SquadPlayer) =>
    `${get(player, 'activeCurrentClub.league')}__${get(player, 'activeCurrentClub.name')}`,
  [Field.PlayerName]: (player: SquadPlayer) => get(player, 'fullName'),
  [Field.PlayerImage]: (player: SquadPlayer) => get(player, 'images.sefImagePng'),
  [Field.PlayerSmcId]: (player: SquadPlayer) => get(player, 'smcId'),
  [Field.PlayerForzasysId]: (player: SquadPlayer) => get(player, 'forzasysId'),
  [Field.PlayerEfId]: (player: SquadPlayer) => get(player, 'efId'),
  [Field.PlayerEsId]: (player: SquadPlayer) => get(player, 'esId'),
  [Field.PlayerFogisId]: (player: SquadPlayer) => get(player, 'fogisId'),
  [Field.PlayerOptaId]: (player: SquadPlayer) => get(player, 'optaId'),
  [Field.PlayerShirtNumber]: (player: SquadPlayer) => get(player, 'activeCurrentClub.shirtNumber'),
  [Field.PlayerPosition]: (player: SquadPlayer) => get(player, 'position.primary'),
  [Field.PlayerNationality]: (player: SquadPlayer) => get(player, 'nationality'),
  [Field.SearchContent]: (player: SquadPlayer) => get(player, 'all-fields'),
};

const { REACT_APP_DEFAULT_LEAGUE_SEASON } = process.env;

const CURRENT_YEAR = parseInt(REACT_APP_DEFAULT_LEAGUE_SEASON as string, 10);

export default function AllPlayersContainer() {
  const { strings } = useStringsContext();
  const params = useParams<{ season: string }>();

  const seasons = Array.from(Array(8).keys()).map((i) => (CURRENT_YEAR - i).toString());
  const selectedSeason = (params.season || CURRENT_YEAR) as string;
  const location = useLocation();
  const history = useHistory();
  const { currentUser } = useUserAuth();
  const query = useAllPlayers({ season: selectedSeason, currentUser });
  const { data, isLoading: loading } = query;
  const teamsSquads = data?.teamsSquads || [];
  const teamsConfig = data?.teamsConfig || [];
  const teamsQuery = useAllTeams();

  const parsed = qs.parse(location.search);

  const [filters, setFilters] = useState<Filter[]>(
    (function getInitialFilters() {
      const filtersArr = (function getFiltersAsArray() {
        if (Array.isArray(parsed.filters)) {
          return parsed.filters;
        }
        if (!Array.isArray(parsed.filters) && !isEmpty(parsed.filters)) {
          return [parsed.filters];
        }
        return [];
      })();

      return filtersArr
        .map((filterStr) => {
          if (filterStr) {
            try {
              return JSON.parse(filterStr);
            } catch (e) {
              return {};
            }
          }
          return {};
        })
        .filter((filter) => !isEmpty(filter));
    })()
  );
  const updateFilters = (newFilters: Filter[]) => {
    setFilters(newFilters);
    history.push(
      `/all-players/${selectedSeason}?${qs.stringify({
        filters: newFilters.filter((f) => !isEmpty(f)).map((f) => JSON.stringify(f)),
      })}`
    );
  };
  const updateSeason = (season: string) => {
    history.push(
      `/all-players/${season}?${qs.stringify({
        filters: filters.filter((f) => !isEmpty(f)).map((f) => JSON.stringify(f)),
      })}`
    );
  };
  const backPage = `/all-players${location.search}`;
  const filteredTeamsSquads = teamsSquads.filter((player) => {
    return filters.every((filter) => {
      const value = filter.value.map((v) => v.trim().toUpperCase());
      const operation = filter.operation;
      function isFilterTrue(getValue: (player: SquadPlayer) => string | number | undefined) {
        const data = (getValue(player) || '').toString().toUpperCase();

        if (operation === Operation.EQ) {
          return value.every((v) => data === v);
        }
        if (operation === Operation.NEQ) {
          return !value.every((v) => data === v);
        }

        if (operation === Operation.CS && filter.filterType === FilterType.FreeText) {
          return value.some((v) => data.includes(v));
        }
        if (operation === Operation.NCS && filter.filterType === FilterType.FreeText) {
          return value.every((v) => !data.includes(v));
        }

        if (operation === Operation.CS) {
          return value.some((v) => data === v);
        }
        if (operation === Operation.NCS) {
          return !value.every((v) => data === v);
        }

        return true;
      }
      const fieldKey = FieldMap[filter.field] || null;
      if (!fieldKey) {
        return true;
      }
      if (filter.field === Field.SearchContent) {
        const fieldMapKeys = Object.keys(FieldMap) as Array<keyof typeof FieldMap>;
        const fields = fieldMapKeys
          .filter((f) => f !== Field.SearchContent)
          .map((f) => FieldMap[f]);
        if (operation === Operation.NCS) {
          return fields.every(isFilterTrue);
        }
        return fields.some(isFilterTrue);
      }

      return isFilterTrue(fieldKey);
    });
  });

  const onEditPlayer = (player: SquadPlayer) => {
    if (!player?.activeCurrentClub?.id) {
      return;
    }
    const teamId = player.activeCurrentClub.id;
    history.push(`/squad/${teamId}/player`, {
      player,
      teamId,
      selectedSeason: parseInt(selectedSeason, 10),
      backPage,
    } as PlayerState);
  };

  const onStatsPlayer = (player: SquadPlayer) => {
    if (!player?.activeCurrentClub?.id) {
      return;
    }
    const teamId = player.activeCurrentClub.id;
    history.push(`/squad/${teamId}/player`, {
      player,
      teamId,
      selectedSeason: parseInt(selectedSeason, 10),
      selectedTab: PLAYER_TABS.STATS,
      backPage,
    } as PlayerState);
  };

  const [selectedPlayer, setSelectedPlayer] = useState<SquadPlayer | null>(null);
  const [showRemovePlayer, setShowRemovePlayer] = useState(false);

  const onDeletePlayer = (player: SquadPlayer) => {
    setSelectedPlayer(player);
    setShowRemovePlayer(true);
  };

  return (
    <ScenePage title={strings.appBar.allPlayers} breadCrumps={[strings.appBar.superAdmin]}>
      <SceneHeader title={strings.appBar.allPlayers}>
        <FormControl>
          <InputLabel size="small">{strings.allPlayers.season}</InputLabel>
          <Select
            size="small"
            value={selectedSeason}
            onChange={(e: SelectChangeEvent) => {
              updateSeason(e.target.value as string);
            }}
            label={strings.allPlayers.season}
          >
            {seasons.map((season) => (
              <MenuItem key={season.toString()} value={season}>
                {season}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Filters
          teamsConfig={teamsConfig}
          teams={teamsQuery.data || []}
          filters={filters}
          removeFilter={(index: number) => {
            updateFilters(filters.slice(0, index).concat(filters.slice(index + 1)));
          }}
          updateFilter={(updatedFilter, index) => {
            updateFilters(
              filters.map((filter, i) => {
                if (i === index) {
                  return {
                    ...filter,
                    ...updatedFilter,
                  };
                }
                return filter;
              })
            );
          }}
          addFilter={(newFilter: Filter) => {
            updateFilters([...filters, newFilter]);
          }}
        />
      </SceneHeader>
      <PlayerList
        players={filteredTeamsSquads}
        onEditPlayer={onEditPlayer}
        onStatsPlayer={onStatsPlayer}
        onDeletePlayer={onDeletePlayer}
        loading={loading}
      />
      {showRemovePlayer &&
        selectedPlayer &&
        selectedPlayer?.activeCurrentClub?.id &&
        selectedSeason && (
          <RemovePlayerModal
            open={showRemovePlayer}
            onClose={() => setShowRemovePlayer(false)}
            onRemovedPlayer={() => {
              query.refetch();
            }}
            player={selectedPlayer}
            season={parseInt(selectedSeason, 10)}
            teamId={selectedPlayer.activeCurrentClub.id}
          />
        )}
    </ScenePage>
  );
}
